import classnames from 'classnames';
import _ from 'lodash';
import moment from 'moment';

/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import PropTypes from 'prop-types';

import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import config from '../../../config';
import './Timeline.scss';
import TimelineBackForwardButton from './TimelineBackForwardButton';
import TimelineController from './TimelineController';
import TimelineMarkers from './TimelineMarkers';
import TimelineRow from './TimelineRow';

const timelineLimit = config.timeline.limit
  ? parseInt(config.timeline.limit)
  : null;

const Wrapper = styled.div`
  @media (min-width: 768px) {
    border: 1px solid #d8dde6;
    border-radius: 0.25rem;
  }
`;

class Timeline extends React.Component {
  static propTypes = {
    range: PropTypes.object,
    offset: PropTypes.object,
    region: PropTypes.string,
    serviceList: PropTypes.object,
    openIncidentModal: PropTypes.func,
    openMaintenanceModal: PropTypes.func
  };

  constructor(props) {
    super(props);

    this.state = {
      offset: moment.duration(),
      range: moment.duration(3, 'days'),
      selectedDate: {}
    };

    this.zoomOut = this.zoomOut.bind(this);
    this.zoomIn = this.zoomIn.bind(this);
    this.back = this.back.bind(this);
    this.forward = this.forward.bind(this);
    this.now = this.now.bind(this);
    this.onCalenderChange = this.onCalenderChange.bind(this);
  }

  zoomOut(newRange) {
    this.setState({
      range: newRange
    });
  }

  zoomIn(newRange) {
    this.setState({
      range: newRange
    });
  }

  back() {
    const currentRange = this.state.range;
    const currentOffset = this.state.offset;

    this.setState({
      offset: moment.duration(currentOffset).add(currentRange),
      selectedDate: {}
    });
  }

  forward() {
    let newOffset = moment
      .duration(this.state.offset)
      .subtract(this.state.range);
    // clamp any offset to positive numbers
    // prevents 'future offset'
    if (newOffset.asMilliseconds() < 0) newOffset = moment.duration(0, 'days');
    this.setState({
      offset: newOffset,
      selectedDate: {}
    });
  }

  now() {
    this.setState({
      offset: moment.duration(0, 'days'),
      selectedDate: {}
    });
  }

  onCalenderChange(selectedDate) {
    const currentDate = new moment();
    const newOffset = moment.duration(currentDate.diff(selectedDate, 'ms'));
    const currentRange = this.state.range.asDays();
    if (currentRange === 7) {
      newOffset.subtract(3, 'days');
    } else if (currentRange === 3) {
      newOffset.subtract(1, 'days');
    }
    this.setState({
      offset: newOffset,
      selectedDate
    });
  }

  render() {
    const { region, serviceList } = this.props;
    const { openIncidentModal, openMaintenanceModal } = this.props;
    const timelineClasses = classnames({
      'slds-grid': true,
      'slds-wrap': true,
      timeline: true,
      'timeline--now': this.state.offset.asMilliseconds() === 0
    });

    return (
      <Wrapper>
        <TimelineController
          range={this.state.range}
          offset={this.state.offset}
          zoomIn={this.zoomIn}
          zoomOut={this.zoomOut}
          now={this.now}
          selectedDate={this.state.selectedDate}
          onCalenderChange={this.onCalenderChange}
          timelineLimit={timelineLimit}
        />
        <div className="status-line-list slds-col slds-size--1-of-1">
          <div className={timelineClasses}>
            <TimelineMarkers
              range={this.state.range}
              offset={this.state.offset}
              selectedDate={this.state.selectedDate}
              region={region}
            />
            <TimelineBackForwardButton
              back={this.back}
              forward={this.forward}
              range={this.state.range}
              offset={this.state.offset}
            />
            {_.map(serviceList, (service, serviceKey) => {
              return (
                <TimelineRow
                  key={serviceKey}
                  serviceKey={serviceKey}
                  incidents={service.incidents}
                  maintenances={service.maintenances}
                  openIncidentModal={openIncidentModal}
                  openMaintenanceModal={openMaintenanceModal}
                  range={this.state.range}
                  offset={this.state.offset}
                />
              );
            })}
          </div>
        </div>
      </Wrapper>
    );
  }
}

export default connect((state) => {
  return {
    region: state.timezonePicker
  };
})(Timeline);
